import {Check, ChevronDown} from 'lucide-react';
import * as React from 'react';
import {TextInput} from 'src/inputs/TextInput';
import Popover from 'src/Layer/Popover';
import {cn} from 'src/lib/utils';

export type FilterDropdownOption = {
  key: string;
  label: string;
  /** Result count shown right-aligned in the menu; omit for pure selects. */
  count?: number;
  /** Menu section header the option renders under; omit for a flat list. */
  group?: string;
  /**
   * Stable identity for the option's group when display names can collide
   * (e.g. two category groups sharing a name). Defaults to `group`, which
   * is fine when labels are unique.
   */
  groupKey?: string;
  /**
   * Leading glyph shown before the label (e.g. an account's logo). When any
   * option carries one every row reserves the slot, so labels stay aligned
   * with the clear row and group headers, which never have icons.
   */
  icon?: React.ReactNode;
  /**
   * Keep the menu open after picking this option in single-select mode. For
   * options that reveal further controls in the menu `footer` (e.g. the date
   * filter's custom range inputs), which would be unreachable if the menu
   * closed on selection.
   */
  keepOpen?: boolean;
};

type MenuRowProps = {
  selected: boolean;
  onClick: () => void;
  label: string;
  count?: number;
  icon?: React.ReactNode;
  /** Reserve the icon slot even when this row has no icon, to keep labels aligned. */
  iconSlot?: boolean;
  /** Group-header styling (small uppercase label). */
  header?: boolean;
};

export const MenuRow = ({
  selected,
  onClick,
  label,
  count,
  icon,
  iconSlot,
  header,
}: MenuRowProps) => (
  <button
    type="button"
    role="menuitem"
    onClick={onClick}
    className={cn(
      'hover:bg-accent/50 flex w-full cursor-pointer items-center gap-2 px-3 py-1.5 text-sm',
      header &&
        'text-muted-foreground pt-2 pb-1 text-xs font-semibold tracking-wider uppercase',
    )}
  >
    <Check className={cn('size-3.5 shrink-0', !selected && 'invisible')} />
    {iconSlot && (
      <span className="flex size-4 shrink-0 items-center justify-center">
        {icon}
      </span>
    )}
    <span className="flex-1 text-left whitespace-nowrap">{label}</span>
    {count != null && (
      <span className="text-muted-foreground pl-4 text-xs tabular-nums">
        {count}
      </span>
    )}
  </button>
);

type FilterPillProps = {
  anchorRef: React.RefCallback<HTMLButtonElement>;
  onClick: () => void;
  /** The dimension (e.g. "Dates"). */
  label: string;
  /** The current selection's text (e.g. "Mar 2024"); null when unfiltered. */
  value: string | null;
  /**
   * Replaces the pill's "label: value" text with a caller-computed string
   * (e.g. "All accounts" / "3 accounts"). Rows still show label and value.
   */
  pillText?: string;
  /** Renders the pill filled, marking an active selection. */
  active: boolean;
  truncateLabel?: boolean;
};

/**
 * How filter triggers render: pills in a header row, or full-width rows in
 * the phone's filter sheet. The same filter components serve both, menus
 * and all; only the trigger changes.
 */
export const FilterTriggerStyle = React.createContext<'pill' | 'row'>('pill');

/**
 * The trigger every filter shares — extracted so controls that render
 * their own menu contents (e.g. DateFilterSelect's year + month panes) still
 * present the exact same trigger as a plain FilterDropdown.
 */
export const FilterPill = ({
  anchorRef,
  onClick,
  label,
  value,
  pillText,
  active,
  truncateLabel,
}: FilterPillProps) => {
  const style = React.useContext(FilterTriggerStyle);
  if (style === 'row') {
    return (
      <button
        ref={anchorRef}
        type="button"
        onClick={onClick}
        className="border-border flex w-full items-center gap-3 border-b py-3 text-left text-sm"
      >
        <span className="shrink-0">{label}</span>
        <span
          className={cn(
            'min-w-0 flex-1 truncate text-right',
            active ? 'text-foreground font-medium' : 'text-muted-foreground',
          )}
        >
          {active ? (value ?? pillText) : 'Any'}
        </span>
        <ChevronDown className="text-muted-foreground size-4 shrink-0" />
      </button>
    );
  }
  return (
    <button
      ref={anchorRef}
      type="button"
      onClick={onClick}
      className={cn(
        // shrink-0 + nowrap so a row of pills keeps its natural widths in a
        // horizontally-scrolling filter bar instead of squeezing.
        'flex items-center gap-1 rounded-full px-3 py-1 text-sm whitespace-nowrap transition-colors',
        truncateLabel ? 'min-w-0' : 'shrink-0',
        active
          ? 'bg-accent text-foreground font-medium'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      <span className={cn(truncateLabel && 'truncate')}>
        {pillText ?? (value != null ? `${label}: ${value}` : label)}
      </span>
      {/* Never the part that goes: without it the pill stops reading as a
          menu, and it costs 14px. */}
      <ChevronDown className="size-3.5 shrink-0" />
    </button>
  );
};

/**
 * The popover box every filter menu shares: capped height so long content
 * scrolls inside the menu instead of running past the viewport, and an
 * optional footer pinned below whatever scrolls. The width cap matches
 * Popover's 8px viewport margins: content wider than the viewport can't be
 * clamped back into it (Popover skips the clamp rather than pushing the left
 * edge off-screen), so the menu has to stay narrow enough to be positionable
 * in the first place.
 */
export const FilterMenuShell = ({
  children,
  footer,
}: {
  children: React.ReactNode;
  footer?: React.ReactNode;
}) => (
  <div
    className={cn(
      'shadow-popover bg-popover text-popover-foreground outline-ring/50',
      'flex max-h-[min(70vh,28rem)] max-w-[calc(100vw-1rem)] min-w-44 flex-col rounded-md outline-1',
    )}
  >
    {children}
    {footer != null && (
      <div className="border-border shrink-0 border-t px-3 py-2">{footer}</div>
    )}
  </div>
);

type BaseProps = {
  /** Dimension name shown on the trigger (e.g. "Status", "Type"). */
  label: string;
  /** Label of the clear-filter row (e.g. "All", "All types"). */
  allLabel: string;
  /** Count shown on the clear-filter row; omit for pure selects. */
  allCount?: number;
  options: FilterDropdownOption[];
  /**
   * Replaces the default `label` (+ `: selection`) text on the pill trigger
   * with a fully caller-computed string — e.g. an account multi-select that
   * reads "All accounts" / an account name / "3 accounts". When omitted the
   * default dimension-plus-selection label is used.
   */
  triggerLabel?: string;
  /**
   * Extra controls pinned below the (scrolling) option list — e.g. the date
   * filter's custom start/end inputs. Keeping them inside the menu means every
   * filter is exactly one pill tall in the row that hosts it.
   */
  footer?: React.ReactNode;
  /**
   * Fires whenever the menu opens or closes, so a caller holding transient
   * menu-only state (e.g. the date filter's revealed custom inputs) can settle
   * it when the menu goes away.
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Let the pill contract and ellipsize its label when the row runs out of
   * room. Off by default: a horizontally-scrolling bar of pills wants each at
   * its natural width, and scrolls instead. A lone pill sharing a row with
   * fixed-width actions (the review header) wants the opposite — an active
   * selection makes the label several times longer than "Dates", and at its
   * natural width it would run under the actions.
   */
  truncateLabel?: boolean;
  /**
   * Pin a type-ahead box above the option list, narrowing it by label. For
   * menus long enough that scanning beats scrolling (the account filter);
   * pointless for a handful of fixed options.
   */
  searchable?: boolean;
};

type SingleProps = BaseProps & {
  multiple?: false;
  /** Current selection: 'all' or an option key. */
  value: string;
  onChange: (value: string) => void;
};

type MultiProps = BaseProps & {
  multiple: true;
  /** Selected option keys; empty means no filter. */
  value: string[];
  onChange: (value: string[]) => void;
};

type Props = SingleProps | MultiProps;

/**
 * GitHub-PR-list-style filter dropdown: a pill trigger showing the dimension
 * (and current selection when one is active), opening a menu of options with
 * counts. Grouped options render under small section headers, in the order
 * groups first appear in `options`.
 *
 * In `multiple` mode options toggle in place (the menu stays open), the
 * clear row empties the selection, and each group header is itself clickable
 * to select/deselect all of that group's options at once.
 */
export const FilterDropdown = (props: Props) => {
  const {
    label,
    allLabel,
    allCount,
    options,
    footer,
    onOpenChange,
    truncateLabel,
    searchable,
  } = props;
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');

  const changeOpen = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
    if (!next) {
      // A stale query would silently pre-filter the next visit's menu.
      setQuery('');
    }
  };

  const selectedKeys = props.multiple ? props.value : [props.value];
  const isSelected = (key: string) => selectedKeys.includes(key);
  const anySelected = props.multiple
    ? props.value.length > 0
    : props.value !== 'all';

  const clearAll = () => {
    if (props.multiple) {
      props.onChange([]);
    } else {
      props.onChange('all');
    }
    changeOpen(false);
  };

  const selectOption = (key: string) => {
    if (props.multiple) {
      // Toggle without closing, so several types can be picked in one visit
      props.onChange(
        isSelected(key)
          ? props.value.filter((k) => k !== key)
          : [...props.value, key],
      );
    } else {
      props.onChange(key);
      if (!options.find((option) => option.key === key)?.keepOpen) {
        changeOpen(false);
      }
    }
  };

  // Rendering works from the query-narrowed list; everything about the
  // selection (labels, group toggles, icon alignment) still reads the full
  // one, so an active filter never changes what a selection means.
  const trimmedQuery = query.trim().toLowerCase();
  const visibleOptions = trimmedQuery
    ? options.filter((option) =>
        option.label.toLowerCase().includes(trimmedQuery),
      )
    : options;

  const identityOf = (option: FilterDropdownOption): string | null =>
    option.group == null ? null : (option.groupKey ?? option.group);

  const ungrouped = visibleOptions.filter((option) => option.group == null);
  const groupOrder: string[] = [];
  const groupLabels = new Map<string, string>();
  for (const option of visibleOptions) {
    const identity = identityOf(option);
    if (identity != null && !groupOrder.includes(identity)) {
      groupOrder.push(identity);
      groupLabels.set(identity, option.group ?? identity);
    }
  }

  const groupKeys = (group: string) =>
    options.filter((option) => identityOf(option) === group).map((o) => o.key);

  const isGroupSelected = (group: string) => {
    const keys = groupKeys(group);
    return keys.length > 0 && keys.every(isSelected);
  };

  const toggleGroup = (group: string) => {
    if (!props.multiple) {
      return;
    }
    const keys = groupKeys(group);
    // Fully selected group toggles off; otherwise select the whole group
    // (keeping any selections from other groups)
    props.onChange(
      isGroupSelected(group)
        ? props.value.filter((k) => !keys.includes(k))
        : [...props.value, ...keys.filter((k) => !isSelected(k))],
    );
  };

  const triggerSelectionLabel = (): string | null => {
    if (!anySelected) {
      return null;
    }
    if (!props.multiple) {
      return options.find((option) => option.key === props.value)?.label ?? null;
    }
    // A selection covering exactly one whole group reads as the group name
    const wholeGroup = groupOrder.find(
      (group) =>
        isGroupSelected(group) && props.value.length === groupKeys(group).length,
    );
    if (wholeGroup != null) {
      return groupLabels.get(wholeGroup) ?? wholeGroup;
    }
    const first = options.find((option) => isSelected(option.key));
    if (!first) {
      return null;
    }
    const extra = props.value.length - 1;
    return extra > 0 ? `${first.label} +${extra}` : first.label;
  };

  const anyIcons = options.some((option) => option.icon != null);

  const renderOption = (option: FilterDropdownOption) => (
    <MenuRow
      key={option.key}
      selected={isSelected(option.key)}
      onClick={() => selectOption(option.key)}
      label={option.label}
      count={option.count}
      icon={option.icon}
      iconSlot={anyIcons}
    />
  );

  const menu = (
    <FilterMenuShell footer={footer}>
      {searchable && (
        <div className="border-border shrink-0 border-b p-2">
          <TextInput
            autoFocus
            placeholder="Search"
            value={query}
            onChange={setQuery}
            className="h-8 text-sm"
          />
        </div>
      )}
      <div className="flex min-h-0 flex-col overflow-y-auto py-1">
        <MenuRow
          selected={!anySelected}
          onClick={clearAll}
          label={allLabel}
          count={allCount}
          iconSlot={anyIcons}
        />
        {trimmedQuery && visibleOptions.length === 0 && (
          <div className="text-muted-foreground px-3 py-1.5 text-sm">
            No matches
          </div>
        )}
        {groupOrder.map((group) => (
          <React.Fragment key={group}>
            {props.multiple ? (
              <MenuRow
                header
                selected={isGroupSelected(group)}
                onClick={() => toggleGroup(group)}
                label={groupLabels.get(group) ?? group}
                iconSlot={anyIcons}
              />
            ) : (
              <div className="text-muted-foreground px-3 pt-2 pb-1 text-xs font-semibold tracking-wider uppercase">
                {groupLabels.get(group) ?? group}
              </div>
            )}
            {visibleOptions
              .filter((option) => identityOf(option) === group)
              .map(renderOption)}
          </React.Fragment>
        ))}
        {/* Ungrouped options trail the sections — matching the grouped
            pickers' browse order (groups first, then the remainder). */}
        {ungrouped.map(renderOption)}
      </div>
    </FilterMenuShell>
  );

  const selectionLabel = triggerSelectionLabel();

  return (
    <Popover
      open={open}
      popoverContents={menu}
      popoverAnchor="bottom-left"
      onOutsideClick={() => changeOpen(false)}
    >
      {(anchorRef: React.RefCallback<HTMLButtonElement>) => (
        <FilterPill
          anchorRef={anchorRef}
          onClick={() => changeOpen(!open)}
          label={label}
          value={props.triggerLabel != null ? (anySelected ? props.triggerLabel : null) : selectionLabel}
          pillText={props.triggerLabel}
          active={anySelected}
          truncateLabel={truncateLabel}
        />
      )}
    </Popover>
  );
};
